/* ==========================================================================
   ENGRAVING — Bible Clock
   --------------------------------------------------------------------------
   1. Drives the Add / No engraving choice in the buy box and the pop-up editor.
   2. Puts the engraving details on the ENGRAVING FEE line, not on the clock.

        Engraving Line 1
        Engraving Line 2
        Engraving Location
        For                 (which clock, e.g. "The Original Bible Clock - Oak")

      This is the whole design. Shopify lets a customer delete either line of a
      two-line cart on its own, and real orders arrived with the engraving text
      still on the clock and the $25 removed - an instruction to do unpaid work.
      Keeping the text on the line that is paid for makes that impossible: there
      is no way to remove the charge and keep the instructions, because they are
      the same line item.

   3. Adds the flat engraving fee to the cart immediately before the clock,
      when a fee product is configured. The fee line carries no visible
      properties - only a hidden _engraving_fee marker.
   4. Blocks add-to-cart when engraving is chosen but nothing was typed.

   Two things this file is careful about:

   * The pop-up is moved to <body> on init, so a sticky product column can
     never trap a position:fixed dialog, and a variant re-render can't destroy
     it mid-edit.
   * Evergreen re-renders the whole product-info block on variant change,
     which throws away the choice cards. State is therefore held in a module
     store keyed by block id and repainted whenever the block reappears.
   ========================================================================== */

(function () {
  'use strict';

  // The block emits its own <script src>. Evergreen re-renders the product
  // info block on every variant change, which re-inserts that tag and makes
  // the browser execute this file again. Without this guard the second copy
  // gets its own state and its own listeners, and the customer's choice is
  // silently lost. Re-entry just re-scans the DOM.
  if (window.__BibleClockEngraving) {
    window.__BibleClockEngraving.init();
    return;
  }

  var PROP_LINE1 = 'properties[Engraving Line 1]';
  var PROP_LINE2 = 'properties[Engraving Line 2]';
  var PROP_LOCATION = 'properties[Engraving Location]';
  // Letters (any language, incl. accents), numbers, and punctuation an engraver
  // can actually cut. Emoji and symbols fall outside \p{L}/\p{N}.
  var SAFE_TEXT = /[^\p{L}\p{N}\p{M} .,'’‘"“”&:;!?()\[\]\-–—·•/#+*@°]/gu;

  var STATE = {}; // uid -> committed state, survives re-renders
  var LIVE = {}; // uid -> instance

  function Engraving(root) {
    this.root = root;
    this.uid = root.dataset.uid;

    this.maxChars = parseInt(root.dataset.maxChars, 10) || 32;
    this.requireText = root.dataset.requireText === 'true';
    this.allowEmoji = root.dataset.allowEmoji === 'true';
    this.feeVariantId = root.dataset.feeVariantId || null;
    // Shown faintly on the photograph until the customer types, so they can see
    // what an engraving looks like. Never enters state, so it can't be ordered.
    this.sampleText = root.dataset.sampleText || '';
    this.feeMatchQuantity = root.dataset.feeMatchQuantity === 'true';
    this.productTitle = root.dataset.productTitle || '';

    this.modal = this.adoptModal(root.dataset.modalId);

    this.summary = root.querySelector('[data-eng-summary]');
    this.error = root.querySelector('[data-eng-error]');
    this.choiceButtons = toArray(root.querySelectorAll('[data-eng-choice]'));

    var m = this.modal;
    this.inputs = m ? toArray(m.querySelectorAll('[data-eng-input]')) : [];
    this.counters = m ? toArray(m.querySelectorAll('[data-eng-count]')) : [];
    this.clearBtns = m ? toArray(m.querySelectorAll('[data-eng-clear]')) : [];
    this.modalError = m ? m.querySelector('[data-eng-modal-error]') : null;
    this.locButtons = m ? toArray(m.querySelectorAll('[data-eng-loc]')) : [];
    this.stages = m ? toArray(m.querySelectorAll('[data-eng-stage]')) : [];
    this.previews = m ? toArray(m.querySelectorAll('[data-eng-preview]')) : [];

    var first = this.locButtons[0];
    var fallback = {
      on: false,
      line1: '',
      line2: '',
      locationId: first ? first.dataset.engLoc : null,
      locationValue: first ? first.dataset.engLocValue : ''
    };

    // Restore whatever the customer had already chosen before a re-render.
    this.state = STATE[this.uid] ? assign({}, STATE[this.uid]) : fallback;
    if (!this.locButtons.length) this.state.on = false;
    this.draft = assign({}, this.state);

    this.bind();
    this.paintChoice();
    this.paintLocation();
    this.paintPreview();
    this.syncForm();
  }

  /* ------------------------------------------------------------- helpers */

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function assign(target) {
    for (var i = 1; i < arguments.length; i++) {
      var src = arguments[i];
      for (var k in src) if (Object.prototype.hasOwnProperty.call(src, k)) target[k] = src[k];
    }
    return target;
  }

  Engraving.prototype.adoptModal = function (id) {
    if (!id) return null;
    // The pop-up is rendered once by the section and moved to <body>, so a
    // sticky product column can never trap a position:fixed dialog and a
    // variant re-render can never destroy it mid-edit.
    var el = document.getElementById(id);
    if (el && el.parentNode !== document.body) document.body.appendChild(el);

    // A link in the editable copy would otherwise navigate away and throw away
    // whatever the customer has typed, so open those in a new tab.
    if (el) {
      toArray(
        el.querySelectorAll(
          '.engraving-modal__sub a, .engraving-modal__helper a, .engraving-modal__disclaimer a'
        )
      ).forEach(function (link) {
        if (!link.getAttribute('href')) return;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
      });
    }

    return el;
  };

  Engraving.prototype.inputByLine = function (n) {
    for (var i = 0; i < this.inputs.length; i++) {
      if ((this.inputs[i].dataset.engInput || '1') === String(n)) return this.inputs[i];
    }
    return null;
  };

  // The two lines as the engraver reads them, blank lines dropped.
  Engraving.prototype.linesOf = function (state) {
    return [state.line1, state.line2].filter(function (v) {
      return v && v.trim();
    });
  };

  Engraving.prototype.getForm = function () {
    return (
      document.querySelector('product-form form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]')
    );
  };

  Engraving.prototype.sanitize = function (value) {
    var out = String(value || '').replace(/\s+/g, ' ');
    if (!this.allowEmoji) out = out.replace(SAFE_TEXT, '');
    return out.slice(0, this.maxChars);
  };

  /* ------------------------------------------------------------ bindings */

  Engraving.prototype.bind = function () {
    var self = this;

    this.choiceButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.dataset.engChoice === 'yes') self.openModal();
        else self.setOff();
      });
    });

    this.locButtons.forEach(function (btn) {
      if (btn.tagName !== 'BUTTON' || btn.dataset.engBound === '1') return;
      btn.dataset.engBound = '1';
      btn.addEventListener('click', function () {
        self.draft.locationId = btn.dataset.engLoc;
        self.draft.locationValue = btn.dataset.engLocValue;
        self.paintLocation();
      });
    });

    this.inputs.forEach(function (input) {
      if (input.dataset.engBound === '1') return;
      input.dataset.engBound = '1';
      var key = 'line' + (input.dataset.engInput || '1');

      input.addEventListener('input', function () {
        var clean = self.sanitize(input.value);
        if (clean !== input.value) {
          var pos = input.selectionStart;
          input.value = clean;
          try {
            input.setSelectionRange(pos - 1, pos - 1);
          } catch (e) {
            /* not supported on this input type */
          }
        }
        self.draft[key] = input.value;
        self.paintPreview();
      });

      input.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        // Enter moves to line 2, then saves.
        var next = self.inputs[self.inputs.indexOf(input) + 1];
        if (next) next.focus();
        else self.save();
      });
    });

    this.clearBtns.forEach(function (btn) {
      if (btn.dataset.engBound === '1') return;
      btn.dataset.engBound = '1';
      var n = btn.dataset.engClear || '1';
      btn.addEventListener('click', function () {
        var input = self.inputByLine(n);
        if (!input) return;
        input.value = '';
        self.draft['line' + n] = '';
        self.paintPreview();
        input.focus();
      });
    });

    if (this.modal && this.modal.dataset.engBound !== '1') {
      this.modal.dataset.engBound = '1';
      toArray(this.modal.querySelectorAll('[data-eng-close]')).forEach(function (el) {
        el.addEventListener('click', function () {
          self.closeModal();
        });
      });
      var saveBtn = this.modal.querySelector('[data-eng-save]');
      if (saveBtn) {
        saveBtn.addEventListener('click', function () {
          self.save();
        });
      }
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.modal && !self.modal.hidden) self.closeModal();
      });
    }

    this.bindForm();
  };

  /* -------------------------------------------------- add-to-cart hookup */

  Engraving.prototype.bindForm = function () {
    var self = this;
    var form = this.getForm();
    if (!form) return;

    var submitButton = form.querySelector('[type="submit"]');
    if (!submitButton || submitButton.dataset.engravingBound === 'true') return;
    submitButton.dataset.engravingBound = 'true';

    // Capture phase on the button runs before the theme's submit handler,
    // which is bound to the form.
    submitButton.addEventListener(
      'click',
      function (e) {
        if (self.busy) return;

        if (!self.validate()) {
          e.preventDefault();
          e.stopPropagation();
          self.openModal();
          return;
        }

        if (!self.state.on || !self.feeVariantId) return;

        e.preventDefault();
        e.stopPropagation();

        self.busy = true;
        submitButton.classList.add('loading');

        self
          .addFee(form)
          .catch(function (err) {
            // Never block the sale because the fee line failed — but say so
            // loudly, because a silent miss looks like "engraving is free".
            if (window.console) {
              console.error(
                '[engraving] THE FEE WAS NOT ADDED. The clock was still added to the cart.\n' +
                'Most common cause: the fee product is out of stock or has inventory tracking on.\n' +
                'Fix: Products -> your fee product -> Inventory -> untick "Track quantity",\n' +
                'or set "Continue selling when out of stock". It must also be published to the Online Store.\n' +
                'Server said:',
                err && err.message ? err.message : err
              );
            }
          })
          .then(function () {
            submitButton.classList.remove('loading');
            if (typeof form.requestSubmit === 'function') form.requestSubmit(submitButton);
            else form.submit();
            window.setTimeout(function () {
              self.busy = false;
            }, 0);
          });
      },
      true
    );
  };

  Engraving.prototype.addFee = function (form) {
    var qtyInput = form.querySelector('[name="quantity"]');
    var qty = this.feeMatchQuantity ? parseInt(qtyInput && qtyInput.value, 10) || 1 : 1;
    var url = (window.routes && window.routes.cart_add_url) || '/cart/add';

    return fetch(url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: Number(this.feeVariantId),
            quantity: qty,
            // The instructions ride on the line that is paid for.
            //
            // They used to sit on the clock line, which reads as the more
            // natural home - the clock is the thing being engraved. But Shopify
            // lets a customer delete either line on its own, and real orders
            // arrived with the text still on the clock and the $25 gone: an
            // instruction to do unpaid work. Text and charge on one line cannot
            // be separated, because deleting one deletes both.
            properties: this.feeProperties(form)
          }
        ]
      })
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          throw new Error(
            'HTTP ' + res.status + ' — ' + (data.description || data.message || 'unknown error')
          );
        }
        return data;
      });
    });
  };

  // What the engraver reads, plus which clock it belongs to.
  //
  // The clock matters because this line no longer sits on the item being
  // engraved: a cart holding an Oak and an Olive with one engraving would
  // otherwise say what to cut and not what to cut it into.
  Engraving.prototype.feeProperties = function (form) {
    var props = { _engraving_fee: 'true' };

    props['Engraving Line 1'] = this.state.line1;
    if (this.state.line2) props['Engraving Line 2'] = this.state.line2;
    props['Engraving Location'] = this.state.locationValue;

    var forWhat = this.clockLabel(form);
    if (forWhat) props['For'] = forWhat;

    return props;
  };

  // The clock as the customer chose it, read from the variant picker so it
  // names the wood rather than just the product.
  Engraving.prototype.clockLabel = function (form) {
    var title = this.productTitle || '';
    var picked = [];

    var picker = document.querySelector('variant-selects');
    if (picker) {
      toArray(picker.querySelectorAll('select')).forEach(function (sel) {
        if (sel.value) picked.push(sel.value);
      });
      toArray(picker.querySelectorAll('input[type="radio"]:checked')).forEach(function (radio) {
        if (radio.value) picked.push(radio.value);
      });
    }

    if (!title && !picked.length) return '';
    if (!picked.length) return title;
    return title ? title + ' - ' + picked.join(' / ') : picked.join(' / ');
  };

  Engraving.prototype.validate = function () {
    if (!this.state.on || !this.requireText) return true;
    if (this.state.line1 && this.state.line1.trim()) return true;
    this.showError('Add the words you’d like engraved, or choose “No engraving”.');
    return false;
  };

  Engraving.prototype.showError = function (message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
  };

  Engraving.prototype.hideError = function () {
    if (this.error) this.error.hidden = true;
    if (this.modalError) this.modalError.hidden = true;
  };

  /* ------------------------------------------------------------- pop-up */

  Engraving.prototype.openModal = function () {
    if (!this.modal) return;
    var self = this;
    this.hideError();

    this.draft = assign({}, this.state);
    this.inputs.forEach(function (input) {
      input.value = self.draft['line' + (input.dataset.engInput || '1')] || '';
    });

    this.paintLocation();
    this.paintPreview();

    this.modal.hidden = false;
    document.body.classList.add('engraving-open');
    this.lastFocus = document.activeElement;

    var input = this.inputs[0];
    var controls = this.modal.querySelector('.engraving-modal__controls');

    window.setTimeout(function () {
      if (input && controls && controls.scrollHeight > controls.clientHeight) {
        var field = input.closest('.engraving-fields') || input.closest('.engraving-field') || input;
        var delta = field.getBoundingClientRect().top - controls.getBoundingClientRect().top;
        controls.scrollTop = Math.max(0, controls.scrollTop + delta - 8);
      }
      if (input) input.focus({ preventScroll: true });
    }, 60);
  };

  Engraving.prototype.closeModal = function () {
    if (!this.modal) return;
    this.modal.hidden = true;
    document.body.classList.remove('engraving-open');

    this.draft = assign({}, this.state);
    this.paintLocation();
    this.paintPreview();
    this.paintChoice();

    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
  };

  Engraving.prototype.save = function () {
    var self = this;
    var line1 = this.sanitize(this.valueOf(1)).trim();
    var line2 = this.sanitize(this.valueOf(2)).trim();

    // Line 2 alone reads as a mistake, so promote it.
    if (!line1 && line2) {
      line1 = line2;
      line2 = '';
      var first = this.inputByLine(1);
      var second = this.inputByLine(2);
      if (first) first.value = line1;
      if (second) second.value = '';
    }

    if (this.requireText && !line1) {
      if (this.modalError) {
        this.modalError.textContent = 'Type the words you’d like engraved.';
        this.modalError.hidden = false;
      }
      var focusTarget = this.inputByLine(1);
      if (focusTarget) focusTarget.focus();
      return;
    }

    this.commit({
      on: true,
      line1: line1,
      line2: line2,
      locationId: this.draft.locationId,
      locationValue: this.draft.locationValue
    });

    this.modal.hidden = true;
    document.body.classList.remove('engraving-open');

    var yes = this.root.querySelector('[data-eng-choice="yes"]');
    if (yes) yes.focus();
  };

  Engraving.prototype.setOff = function () {
    this.inputs.forEach(function (input) {
      input.value = '';
    });
    this.commit({
      on: false,
      line1: '',
      line2: '',
      locationId: this.state.locationId,
      locationValue: this.state.locationValue
    });
    this.paintPreview();
  };

  Engraving.prototype.valueOf = function (n) {
    var input = this.inputByLine(n);
    return input ? input.value : '';
  };

  Engraving.prototype.commit = function (next) {
    this.state = next;
    STATE[this.uid] = assign({}, next);
    this.draft = assign({}, next);
    this.syncForm();
    this.paintChoice();
    this.hideError();
  };

  /* ---------------------------------------------------------- rendering */

  Engraving.prototype.paintChoice = function () {
    var on = this.state.on;

    this.choiceButtons.forEach(function (btn) {
      var isYes = btn.dataset.engChoice === 'yes';
      btn.setAttribute('aria-checked', (isYes ? on : !on) ? 'true' : 'false');
    });

    if (!this.summary) return;

    var lines = this.linesOf(this.state);

    if (on && lines.length) {
      this.summary.textContent = '';
      lines.forEach(function (line, i) {
        var quote = document.createElement('b');
        quote.textContent = '“' + line + '”';
        this.summary.appendChild(quote);
        this.summary.appendChild(document.createElement('br'));
      }, this);
      var where = document.createElement('span');
      where.textContent = this.state.locationValue;
      this.summary.appendChild(where);
      this.summary.hidden = false;
    } else {
      this.summary.hidden = true;
    }
  };

  Engraving.prototype.paintLocation = function () {
    var id = this.draft.locationId;

    this.locButtons.forEach(function (btn) {
      if (btn.tagName !== 'BUTTON') return;
      btn.setAttribute('aria-pressed', btn.dataset.engLoc === id ? 'true' : 'false');
    });

    this.stages.forEach(function (stage) {
      stage.classList.toggle('engraving__stage--hidden', stage.dataset.engStage !== id);
    });
  };

  Engraving.prototype.paintPreview = function () {
    var self = this;
    // white-space: pre-wrap on .engraving__text turns the newline into a
    // second engraved line, positioned by the same location settings.
    var text = this.linesOf(this.draft).join('\n');

    // Nothing typed at all: show the sample. The moment either line has
    // content the sample goes, so it can never be mistaken for the order.
    var sample = !text && this.sampleText ? this.sampleText : '';

    this.previews.forEach(function (el) {
      el.textContent = text || sample;
      el.classList.toggle('engraving__text--sample', Boolean(sample));
    });

    this.counters.forEach(function (el) {
      var n = el.dataset.engCount || '1';
      el.textContent = String(self.valueOf(n).length);
    });

    this.clearBtns.forEach(function (btn) {
      var n = btn.dataset.engClear || '1';
      btn.hidden = self.valueOf(n).length === 0;
    });
  };

  /* ------------------------------------------------- form property sync */

  // Nothing is written to the clock's form any more: the instructions travel
  // with the fee. This also clears anything an older version of this script
  // left behind in a cart the customer still has open.
  Engraving.prototype.syncForm = function () {
    var form = this.getForm();
    if (!form) return;
    this.setInput(form, PROP_LINE1, null);
    this.setInput(form, PROP_LINE2, null);
    this.setInput(form, PROP_LOCATION, null);
  };

  Engraving.prototype.setInput = function (form, name, value) {
    var el = form.querySelector('input[name="' + name + '"][data-engraving-field]');

    if (value === null || value === '') {
      if (el && el.parentNode) el.parentNode.removeChild(el);
      return;
    }
    if (!el) {
      el = document.createElement('input');
      el.type = 'hidden';
      el.name = name;
      el.setAttribute('data-engraving-field', '');
      form.appendChild(el);
    }
    el.value = value;
  };

  /* ----------------------------------------------------------- lifecycle */

  // Where the cards belong: immediately above Quantity, under the wood picker.
  function findAnchor() {
    return (
      document.querySelector('.product-form__quantity') ||
      document.querySelector('[id^="Quantity-Form-"]') ||
      document.querySelector('product-form') ||
      null
    );
  }

  // The section renders the cards hidden and outside the product-info block,
  // so the source markup survives every variant re-render. Each time the buy
  // box loses its copy we clone a fresh one back in.
  function placeCards() {
    toArray(document.querySelectorAll('[data-engraving-source]')).forEach(function (src) {
      var tpl = src.querySelector('[data-engraving-cards]');
      if (!tpl) return;

      var uid = tpl.dataset.uid;
      var placed = null;
      toArray(document.querySelectorAll('[data-engraving-cards][data-uid="' + uid + '"]')).forEach(
        function (el) {
          if (!src.contains(el)) placed = el;
        }
      );
      if (placed && document.body.contains(placed)) return;

      var anchor = findAnchor();
      if (!anchor || !anchor.parentNode) return;

      var clone = tpl.cloneNode(true);
      clone.hidden = false;
      clone.removeAttribute('data-engraving-ready');
      anchor.parentNode.insertBefore(clone, anchor);
    });
  }

  function init() {
    placeCards();

    toArray(document.querySelectorAll('[data-engraving-root]')).forEach(function (root) {
      // The hidden source copy is a template, never a live instance.
      if (root.closest && root.closest('[data-engraving-source]')) return;
      if (root.dataset.engravingReady === 'true') return;
      root.dataset.engravingReady = 'true';
      LIVE[root.dataset.uid] = new Engraving(root);
    });

    // A re-render can drop the hidden inputs; put them back.
    Object.keys(LIVE).forEach(function (uid) {
      if (document.body.contains(LIVE[uid].root)) {
        LIVE[uid].bindForm();
        LIVE[uid].syncForm();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Evergreen replaces the product-info markup on variant change, and the
  // theme editor replaces whole sections. Both land here.
  if ('MutationObserver' in window) {
    var scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.setTimeout(function () {
        scheduled = false;
        init();
      }, 60);
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);

  window.__BibleClockEngraving = { init: init, state: STATE };
})();